/**
 * Where a table's remembered view lives.
 *
 * Every key written here names the user and the workspace it belongs to. The store it
 * replaces was a single bucket per table name, so a filter chosen in one workspace came
 * straight back in the next one, and a column layout outlived the account that picked it.
 *
 * Keys are also in `localStorage` rather than `sessionStorage`, because a view is meant to
 * be remembered for good in the workspace it was set in, not until the tab closes.
 */

/** Namespace for everything this module owns. Also the marker that a key is already scoped. */
const NAMESPACE = 'sdt_v2';

/**
 * Segment separator. `encodeURIComponent` escapes `:`, so an encoded segment can never
 * contain this and two different scopes can never build the same key.
 */
const SEPARATOR = '::';

/** Prefix of every key the old, unscoped store wrote. Cleared on first load after this change. */
const LEGACY_PREFIX = 'sdt_';

/**
 * Upper bound on remembered views, counted per user, workspace and table. Someone who works
 * across many workspaces accumulates one entry per table in each of them, and `localStorage`
 * evicts nothing on its own. Least recently used views go first.
 */
const MAX_VIEWS = 60;

/** What a scoped key is built from. Both parts are required: a key with neither is the bug. */
export interface TableStorageScope {
  userKey: string;
  workspaceId: string;
}

/**
 * `state` is the sorter, filters and search this module writes. `cols` is the column layout,
 * written by ProTable itself under a key we hand it. `extra` is a page-level filter that does
 * not belong to a column, such as the vulnerability list's stat cards.
 */
export type TableStateKind = 'state' | 'cols' | 'extra';

export interface PersistedTableState {
  sorter?: { field?: string; order?: 'ascend' | 'descend' };
  filters?: Record<string, any>;
  keyword?: string;
  /** Write time, used to decide what to drop once `MAX_VIEWS` is reached. */
  updatedAt?: number;
}

/** Extras are wrapped so they carry a write time the same way table state does. */
interface PersistedExtra<T> {
  value: T;
  updatedAt?: number;
}

/**
 * Storage access can throw outright, not just return null: Safari in private browsing and a
 * browser set to block site data both raise on the property itself. A table that cannot
 * remember its filters is a smaller problem than a table that will not render.
 */
const getStore = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getSessionStore = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const buildScopedKey = (
  scope: TableStorageScope,
  kind: TableStateKind,
  name: string
): string => {
  const user = encodeURIComponent(scope.userKey);
  const workspace = encodeURIComponent(scope.workspaceId);
  return [NAMESPACE, kind, user, workspace, name].join(SEPARATOR);
};

const readRaw = (key: string): unknown => {
  const store = getStore();
  if (!store) return null;
  try {
    const stored = store.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn('Failed to read table state', e);
    return null;
  }
};

const writeRaw = (key: string, payload: unknown): void => {
  const store = getStore();
  if (!store) return;
  try {
    // Only a key that did not exist can push the store past the cap, so pruning on an
    // update would scan storage on every table request for nothing.
    const isNew = store.getItem(key) === null;
    store.setItem(key, JSON.stringify(payload));
    if (isNew) pruneStoredViews();
  } catch (e) {
    console.warn('Failed to save table state', e);
  }
};

export const removeStoredKey = (key: string | null): void => {
  if (!key) return;
  const store = getStore();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch (e) {
    console.warn('Failed to clear table state', e);
  }
};

export const readTableState = (key: string | null): PersistedTableState | null => {
  if (!key) return null;
  const parsed = readRaw(key);
  return parsed && typeof parsed === 'object'
    ? (parsed as PersistedTableState)
    : null;
};

export const writeTableState = (
  key: string | null,
  state: PersistedTableState
): void => {
  if (!key) return;
  writeRaw(key, {
    ...state,
    updatedAt: Date.now()
  });
};

/**
 * Read a page-level extra, falling back when the key is unscoped, absent or holds something
 * of the wrong shape. Callers pass `isValid` because a stale payload written by an older
 * build is indistinguishable from a fresh one until it is inspected.
 */
export const readScopedValue = <T>(
  key: string | null,
  fallback: T,
  isValid: (value: unknown) => boolean
): T => {
  if (!key) return fallback;
  const parsed = readRaw(key) as PersistedExtra<T> | null;
  if (!parsed || typeof parsed !== 'object' || !('value' in parsed)) {
    return fallback;
  }
  return isValid(parsed.value) ? parsed.value : fallback;
};

export const writeScopedValue = (key: string | null, value: unknown): void => {
  if (!key) return;
  writeRaw(key, {
    value,
    updatedAt: Date.now()
  });
};

const readUpdatedAt = (store: Storage, key: string): number => {
  try {
    const stored = store.getItem(key);
    if (!stored) return 0;
    const parsed = JSON.parse(stored);
    const updatedAt = parsed?.updatedAt;
    return typeof updatedAt === 'number' ? updatedAt : 0;
  } catch {
    return 0;
  }
};

/**
 * Drop the least recently used views once there are more than `MAX_VIEWS` of them.
 *
 * A view is every key sharing a user, workspace and table name, so a table's filters and its
 * column layout are dropped together rather than leaving one without the other. The column
 * layout carries no write time of its own, hence taking the newest time in the group.
 */
const pruneStoredViews = (): void => {
  const store = getStore();
  if (!store) return;

  const groups = new Map<string, { keys: string[]; updatedAt: number }>();

  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (!key || !key.startsWith(`${NAMESPACE}${SEPARATOR}`)) continue;

      const parts = key.split(SEPARATOR);
      // NAMESPACE, kind, user, workspace, name. Anything shorter is not a view.
      if (parts.length < 5) continue;

      const groupId = parts.slice(2).join(SEPARATOR);
      const group = groups.get(groupId) ?? {
        keys: [],
        updatedAt: 0
      };
      group.keys.push(key);
      group.updatedAt = Math.max(group.updatedAt, readUpdatedAt(store, key));
      groups.set(groupId, group);
    }

    if (groups.size <= MAX_VIEWS) return;

    // Collected first, deleted after: removing while walking `store.key(i)` reindexes it.
    Array.from(groups.values())
      .sort((a, b) => a.updatedAt - b.updatedAt)
      .slice(0, groups.size - MAX_VIEWS)
      .forEach((group) => group.keys.forEach((key) => store.removeItem(key)));
  } catch (e) {
    console.warn('Failed to prune table state', e);
  }
};

/**
 * Remove everything the unscoped store left behind, in both places it used to live: table
 * state in `sessionStorage`, column layouts in `localStorage`.
 *
 * These entries cannot be migrated. Nothing in them says which user or workspace they came
 * from, so keeping them would mean handing one person's column layout to whoever signs in
 * next. Each table resets to its defaults once, and is remembered properly from then on.
 */
export const purgeLegacyTableState = (): void => {
  [getStore(), getSessionStore()].forEach((store) => {
    if (!store) return;
    try {
      const stale: string[] = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (!key) continue;
        if (
          key.startsWith(LEGACY_PREFIX) &&
          !key.startsWith(`${NAMESPACE}${SEPARATOR}`)
        ) {
          stale.push(key);
        }
      }
      stale.forEach((key) => store.removeItem(key));
    } catch (e) {
      console.warn('Failed to clear legacy table state', e);
    }
  });
};
